import { NavigationProp, ParamListBase, useNavigation } from "@react-navigation/native";
import moment from "moment";
import React from "react";
import { FlatList, Pressable, StyleProp, StyleSheet, TouchableOpacity, View, ViewProps, ViewStyle } from "react-native";
import { useSelector } from "react-redux";

import { Avatar } from "src/components/Chat/components";
import { PdText } from "src/components/layout";
import { useIsMobile, useUserProfile } from "src/hooks/ui";
import { useAppDispatch } from "src/redux";
import { kColors } from "src/kColors";
import { kRoute } from "src/kRoute";
import { kStyleConst } from "src/kStyle";
import { setCurrentThread } from "src/redux/messages/actions";
import { kDefaultProfileIconUri } from "src/util/constants";
import * as userDb from "src/db/user.db";

import { tMessage, tThread } from "tmt_api/src/types/types.validator";
import { kBotMessageThreadRecipientStr, kHelpMessageThreadRecipientStr } from "tmt_api/src/kConsts";
import { PdSearchInput } from "src/components/v2/ui";
import { tPressableState } from "src/components/types";
import { PdTableMobileItemSeparator } from "src/components/v2/layout";
import BrandTermsLink from "src/components/Chat/BrandTermsLink";
import AcknowledgementBox from "src/components/Chat/AcknowledgementBox";
import { queryBrandRepConnection } from "src/db/connection.db";
import { EmptyMessage } from "src/components";
import { Chat } from "src/components";
import WriteAnOrderButton from "src/components/Chat/WriteAnOrderButton";

moment.locale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s",
    s: "1s",
    ss: "%ss",
    m: "1m",
    mm: "%dm",
    h: "1h",
    hh: "%dh",
    d: "1d",
    dd: "%dd",
    M: "1mo",
    MM: "%dmo",
    y: "1y",
    yy: "%dy",
  },
});

const PdDivider: React.FC<ViewProps & { orientation?: "horizontal" | "vertical" }> = ({
  orientation = "vertical",
  style,
  ...rest
}) => (
  <View style={[orientation === "vertical" ? styles.pdDividerVertical : styles.pdDividerHorizontal, style]} {...rest} />
);

const EmptyThread: React.FC = () => <EmptyMessage message="There are no messages to view" />;

const ThreadItem: React.FC<{ item: tThread; avatarUrl?: string; style?: StyleProp<ViewStyle> }> = ({
  item,
  avatarUrl,
  style,
}) => {
  const navigation = useNavigation();
  const rUser = useSelector((state) => state.UserReducer.profile);
  const dispatch = useAppDispatch();
  const rSelectedId = useSelector((state) => state.MessageThreadsReducer.rCurrentThreadId);
  const isMobile = useIsMobile();
  const threadDetails = React.useMemo(() => {
    const parts = item.recipient.split("/") ?? ["", ""];
    const accountType = parts[0];
    const accountId = parts[1];
    return { accountType, accountId };
  }, [item.recipient]);
  const avatar = avatarUrl ?? item?.recipientAvatar ?? kDefaultProfileIconUri;
  const isActive = item.id === rSelectedId && !isMobile;

  const handleOnPress = () => {
    dispatch(setCurrentThread(item.id));
    if (isMobile) {
      navigation.navigate(kRoute.P04_01, { id: item.id });
    }
  };

  const handleOnPressAvatar = async () => {
    if (threadDetails.accountType === "boutiques") {
      if (rUser.accountType === "tmtAdmin") navigation.navigate(kRoute.P06_10, { retailerId: threadDetails.accountId });
      else navigation.navigate(kRoute.P10, { id: threadDetails.accountId, connectionId: item.id });
    }

    if (threadDetails.accountType === "brands") {
      if (rUser.accountType === "tmtAdmin") navigation.navigate(kRoute.P06_09, { brandId: threadDetails.accountId });
      else navigation.navigate(kRoute.P100, { brand: item.recipientStr });
    }

    if (threadDetails.accountType === "reps") {
      if (rUser.accountType === "tmtAdmin") navigation.navigate(kRoute.P06_11, { repId: threadDetails.accountId });
      else navigation.navigate(kRoute.P20, { id: threadDetails.accountId });
    }
  };

  return (
    <Pressable onPress={handleOnPress} style={style}>
      {({ hovered, pressed }: tPressableState) => (
        <View
          style={[
            styles.threadItem,
            hovered || isActive ? styles.threadItemHovered : {},
            pressed ? styles.threadItemPressed : {},
          ]}
        >
          {item.isUnread && <View style={styles.unreadDot} />}
          <TouchableOpacity onPress={handleOnPressAvatar}>
            <Avatar uri={avatar} name={item.recipientStr} />
          </TouchableOpacity>
          <View style={[styles.flex1, styles.flexRow, styles.alignCenter, styles.ml3]}>
            <PdText numberOfLines={1} style={item.isUnread ? styles.threadItemTitleUnread : styles.threadItemTitle}>
              {item.recipientStr}
            </PdText>
            <PdText style={styles.threadItemDate}>
              {moment(new Date(item.lastMessageDate.seconds * 1000)).fromNow()}
            </PdText>
          </View>
        </View>
      )}
    </Pressable>
  );
};

const useThreadsData = () => {
  const rThreads = useSelector((state) => state.MessageThreadsReducer.rThreads);
  const threads: tThread[] = [];
  for (const thread of rThreads) {
    if (
      thread.recipientStr !== kHelpMessageThreadRecipientStr &&
      thread.recipientStr !== kBotMessageThreadRecipientStr
    ) {
      threads.push({ ...thread, isUnread: !!thread.isUnread });
    }
  }
  return threads.sort((a, b) => {
    const sort1 = (a.isUnread ? 0 : 1) - (b.isUnread ? 0 : 1);
    if (sort1 !== 0) return sort1;
    return b.lastMessageDate.seconds - a.lastMessageDate.seconds;
  });
};

const Threads: React.FC = () => {
  const isMobile = useIsMobile();
  const rThreads = useThreadsData();
  const rHelpThread = useSelector((state) =>
    state.MessageThreadsReducer.rThreads.find((myThread) => myThread.recipientStr === kHelpMessageThreadRecipientStr),
  );
  const rBotThread = useSelector((state) =>
    state.MessageThreadsReducer.rThreads.find((myThread) => myThread.recipientStr === kBotMessageThreadRecipientStr),
  );
  const rChatHelpAvatarUrl = useSelector((state) => state.ConfigsReducer.data.urls?.chatHelpAvatarUrl);
  const [searchTerm, setSearchTerm] = React.useState("");

  const threads = React.useMemo(
    () =>
      searchTerm
        ? rThreads.filter(
            (thread) => thread.recipientStr.toLocaleLowerCase().indexOf(searchTerm.toLocaleLowerCase()) !== -1,
          )
        : rThreads,
    [rThreads, searchTerm],
  );

  return (
    <View style={isMobile ? styles.mobileThreads : styles.threads}>
      <PdText variant="subheader" style={styles.mb4}>
        Messages
      </PdText>
      <PdSearchInput
        value={searchTerm}
        onChangeText={setSearchTerm}
        placeholder="Search your messages"
        style={styles.mb4}
      />
      {rHelpThread && <ThreadItem item={rHelpThread} avatarUrl={rChatHelpAvatarUrl} style={styles.mb2} />}
      <FlatList
        ItemSeparatorComponent={() => <PdTableMobileItemSeparator />}
        style={styles.threadList}
        contentContainerStyle={[styles.threadListContainer, threads.length === 0 ? styles.flex1 : {}]}
        data={threads}
        renderItem={({ item }) => <ThreadItem item={item} />}
        keyExtractor={(item) => JSON.stringify(item)}
        ListEmptyComponent={EmptyThread}
      />
      {rBotThread && <ThreadItem item={rBotThread} style={[styles.mt2, styles.mb4]} />}
    </View>
  );
};

const P04_Messages: React.FC<{ navigation: NavigationProp<ParamListBase> }> = ({ navigation }) => {
  const { isAdmin, isBrand, isRetailer, isRep } = useUserProfile();
  const rUser = useSelector((state) => state.UserReducer.profile);
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const rCompany = useSelector(({ CompanyReducer }) => CompanyReducer);
  const rThreads = useSelector((state) => state.MessageThreadsReducer.rThreads);
  const rSelectedId = useSelector((state) => state.MessageThreadsReducer.rCurrentThreadId);
  const isMobile = useIsMobile();
  const [title, setTitle] = React.useState("");
  const currentThread = React.useMemo(
    () => rThreads.find((thread) => thread.id === rSelectedId),
    [rSelectedId, rThreads],
  );
  const isUserThread = React.useMemo(
    () =>
      currentThread?.recipientStr !== kBotMessageThreadRecipientStr &&
      currentThread?.recipientStr !== kHelpMessageThreadRecipientStr,
    [currentThread?.recipientStr],
  );
  const threadDetails = React.useMemo(() => {
    const parts = currentThread?.recipient.split("/") ?? ["", ""];
    const accountType = parts[0];
    const accountId = parts[1];
    return { accountType, accountId };
  }, [currentThread?.recipient]);

  const handleOnPressTitle = async () => {
    if (threadDetails.accountType === "boutiques") {
      if (isAdmin) navigation.navigate(kRoute.P06_10, { retailerId: threadDetails.accountId });
      else navigation.navigate(kRoute.P10, { id: threadDetails.accountId, connectionId: currentThread?.id });
    }

    if (threadDetails.accountType === "brands") {
      if (isAdmin) navigation.navigate(kRoute.P06_09, { brandId: threadDetails.accountId });
      else navigation.navigate(kRoute.P100, { brand: currentThread?.recipientStr });
    }

    if (threadDetails.accountType === "reps") {
      if (isAdmin) navigation.navigate(kRoute.P06_11, { repId: threadDetails.accountId });
      else navigation.navigate(kRoute.P20, { id: threadDetails.accountId });
    }
  };

  const handleOnPressWriteAnOrder = async () => {
    if (rUser.accountType === "brand" && rCompany.type === "brand") {
      navigation.navigate(kRoute.P100, { brand: rCompany.name });
    } else if (rUser.accountType === "rep") {
      const connectionsQuery = await queryBrandRepConnection({ repId: rCompanyId }).get();
      if (connectionsQuery.size > 1) {
        navigation.navigate(kRoute.P99);
      } else if (connectionsQuery.docs[0]) {
        navigation.navigate(kRoute.P100, { brand: connectionsQuery.docs[0].data().business.name });
      }
    }
  };

  const handleOnPressMessageItemParts = async (item: tMessage) => {
    const userCompanyId = rUser.companyRefPath.split("/")?.[1] ?? "";
    if (item.user._id === userCompanyId) return;

    if (item.user.type === "retailer") {
      if (isAdmin) navigation.navigate(kRoute.P06_10, { retailerId: item.user._id });
      else navigation.navigate(kRoute.P10, { id: item.user._id, connectionId: item.threadId });
    }

    if (item.user.type === "brand") {
      if (isAdmin) navigation.navigate(kRoute.P06_09, { brandId: item.user._id });
      else navigation.navigate(kRoute.P100, { brand: item.user.name });
    }

    if (item.user.type === "rep") {
      if (isAdmin) navigation.navigate(kRoute.P06_11, { repId: item.user._id });
      else navigation.navigate(kRoute.P20, { id: item.user._id });
    }
  };

  React.useEffect(() => {
    if (currentThread) {
      if (!isUserThread) {
        setTitle(currentThread.recipientStr);
        return;
      }

      (async () => {
        const usersQuery = await userDb.queryUser({ companyRefPath: currentThread?.recipient }).get();
        const user = usersQuery.docs[0]?.data();
        const userName = user ? `${user.firstName} ${user.lastName}` : "";
        if (currentThread.recipientStr !== userName) {
          setTitle(`${currentThread.recipientStr}, ${userName}`);
        } else setTitle(currentThread.recipientStr);
      })();
    }
  }, [currentThread, isUserThread]);

  if (isMobile) {
    return <Threads />;
  }

  return (
    <View style={[styles.flex1, styles.flexRow]}>
      <Threads />
      <PdDivider orientation="horizontal" />
      <View style={styles.flex1}>
        <View style={styles.headerChatContainer}>
          <View style={styles.headerChatLeftContainer}>
            {title && (
              <TouchableOpacity onPress={handleOnPressTitle}>
                <PdText numberOfLines={1} variant="body-lg" style={styles.headerChatTitle}>
                  {title}
                </PdText>
              </TouchableOpacity>
            )}
            {isRetailer && threadDetails.accountType === "brands" && (
              <BrandTermsLink brandId={currentThread?.recipient.split("/")?.[1]} />
            )}
          </View>
          {(isBrand || isRep) && <WriteAnOrderButton onPress={handleOnPressWriteAnOrder} />}
        </View>
        <PdDivider />
        {(isBrand || isRetailer) && <AcknowledgementBox />}
        <Chat
          thread={currentThread}
          onPressMessageUserName={handleOnPressMessageItemParts}
          onPressMessageUserAvatar={handleOnPressMessageItemParts}
        />
      </View>
    </View>
  );
};

export const P04 = React.memo(P04_Messages);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  headerChatContainer: {
    alignItems: "flex-end",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingBottom: kStyleConst.kSpacing * 4,
    paddingHorizontal: kStyleConst.kSpacing * 8,
    paddingTop: kStyleConst.kSpacing * 5,
  },
  headerChatLeftContainer: {
    flex: 1,
    marginRight: kStyleConst.kSpacing * 2,
  },
  headerChatTitle: {
    textDecorationLine: "underline",
  },
  mb2: {
    marginBottom: kStyleConst.kSpacing * 2,
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  ml3: {
    marginLeft: kStyleConst.kSpacing * 3,
  },
  mobileThreads: {
    flex: 1,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingTop: kStyleConst.kSpacing * 4,
  },
  mt2: {
    marginTop: kStyleConst.kSpacing * 2,
  },
  pdDividerHorizontal: {
    backgroundColor: kColors.gray300,
    height: "100%",
    width: StyleSheet.hairlineWidth,
  },
  pdDividerVertical: {
    backgroundColor: kColors.gray300,
    height: StyleSheet.hairlineWidth,
  },
  threadItem: {
    alignItems: "center",
    borderRadius: kStyleConst.kSpacing,
    flexDirection: "row",
    paddingHorizontal: kStyleConst.kSpacing * 3,
    paddingVertical: kStyleConst.kSpacing * 1.5,
  },
  threadItemDate: {
    color: kColors.gray500,
    marginLeft: "auto",
  },
  threadItemHovered: {
    backgroundColor: kColors.lightGray,
  },
  threadItemPressed: {
    opacity: 0.6,
  },
  threadItemTitle: {
    marginRight: kStyleConst.kSpacing * 2,
  },
  threadItemTitleUnread: {
    fontWeight: "500",
    marginRight: kStyleConst.kSpacing * 2,
  },
  threadList: {
    marginHorizontal: -(kStyleConst.kSpacing * 8),
  },
  threadListContainer: {
    marginHorizontal: kStyleConst.kSpacing * 8,
  },
  threads: {
    minWidth: 350,
    paddingHorizontal: kStyleConst.kSpacing * 8,
    paddingTop: kStyleConst.kSpacing * 4,
  },
  unreadDot: {
    backgroundColor: kColors.brandCoral,
    borderRadius: 999,
    height: 5,
    left: 5,
    marginVertical: "auto",
    position: "absolute",
    width: 5,
  },
});
